import { useMemo, useState } from 'react';
import { Stack, Button, Group, SimpleGrid, Text, Badge } from '@repletoryreach/sdk-ui';
import { Alert, EmptyState, PageHeader, TableShell, DataTable, KpiFilterRow, type KpiFilterItem } from '@repletoryreach/sdk-ui/data-display';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { StatCard } from '../../../components/StatCard';
import { TableFooter } from '../../../components/TableFooter';
import { TenantName } from '../../../components/TenantName';
import { usePersistedState } from '../../../lib/usePersistedState';
import { formatMinor, formatDateTime, formatInteger } from '../../../lib/format';
import type { Indexed } from '../../../lib/types';
import { useAffiliateSummary, useAffiliates, useRunAccrual } from '../../../data/useAffiliates';
import { AffiliateDrawer } from './components/AffiliateDrawer';
import { useT } from '../../../i18n/I18nProvider';

/**
 * Partners: who is in the programme, what they are owed, and who is waiting
 * for a decision.
 *
 * The row carries the money the partner is never shown alongside the money
 * they are, because an operator approving a payment needs both: what was
 * earned, and what it was earned on.
 */
export default function PartnersPage() {
  const t = useT();
  const summary = useAffiliateSummary();
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState('');
  const list = useAffiliates({ status, q: search || undefined });
  const accrual = useRunAccrual();
  const [pageSize, setPageSize] = usePersistedState<number>('admin.partners.pageSize', 25);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<string | null>(null);

  const s = summary.data?.data;
  const rows = useMemo(
    () => (list.data?.data ?? []).map((a, i) => ({ ...a, _i: i })),
    [list.data]
  );
  const paged = rows.slice((page - 1) * pageSize, page * pageSize);

  const kpis: KpiFilterItem[] = [
    { id: 'all', label: t('partners.filter.all'), value: formatInteger(rows.length) },
    { id: 'pending_review', label: t('partners.filter.pending'), value: formatInteger(s?.pending_review ?? 0), tone: 'pending' },
    { id: 'active', label: t('partners.filter.active'), value: formatInteger(s?.active ?? 0), tone: 'healthy' },
    { id: 'suspended', label: t('partners.filter.suspended'), value: formatInteger(s?.suspended ?? 0), tone: 'warning' }
  ];

  async function runAccrual() {
    try {
      const res = await accrual.mutateAsync(undefined);
      notifySuccess(t('partners.accrual.done', {
        rows: String(res.data.commissions),
        amount: formatMinor(res.data.amount_minor, 'ZAR')
      }));
    } catch {
      notifyError(t('partners.accrual.failed'));
    }
  }

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('partners.eyebrow')}
        title={t('partners.title')}
        description={t('partners.description')}
        actions={
          <Button variant="light" onClick={runAccrual} loading={accrual.isPending}>
            {t('partners.accrual.run')}
          </Button>
        }
      />

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
        <StatCard label={t('partners.stat.active')} value={formatInteger(s?.active ?? 0)} hint={t('partners.stat.activeHint')} loading={summary.isLoading} />
        <StatCard label={t('partners.stat.referred')} value={formatInteger(s?.referred_companies ?? 0)} hint={t('partners.stat.referredHint')} loading={summary.isLoading} />
        <StatCard label={t('partners.stat.earned')} value={formatMinor(s?.commission_this_month_minor ?? 0, 'ZAR')} hint={t('partners.stat.earnedHint')} loading={summary.isLoading} tone="primary" />
        <StatCard label={t('partners.stat.owed')} value={formatMinor(s?.owed_minor ?? 0, 'ZAR')} hint={t('partners.stat.owedHint')} loading={summary.isLoading} tone={s && s.withdrawals_waiting > 0 ? 'warning' : 'neutral'} />
      </SimpleGrid>

      <KpiFilterRow
        items={kpis}
        value={status ?? 'all'}
        onChange={(next) => { setStatus(!next || next === 'all' ? undefined : next); setPage(1); }}
        filteringLabel={t('common.filtering')}
      />

      <TableShell
        search={search}
        onSearch={setSearch}
        searchPlaceholder={t('partners.searchPlaceholder')}
        totalLabel={t('partners.total', { count: formatInteger(rows.length) })}
        loading={list.isLoading}
        isEmpty={!list.isLoading && rows.length === 0}
        empty={
          <EmptyState
            title={status || search ? t('partners.noneMatch') : t('partners.emptyTitle')}
            description={status || search ? t('partners.noneMatchBody') : t('partners.emptyBody')}
          />
        }
        error={
          list.isError ? (
            <Alert color="error" role="alert">
              {t('partners.loadFailed')}{' '}
              <Button variant="subtle" size="sm" onClick={() => void list.refetch()}>{t('action.retry')}</Button>
            </Alert>
          ) : undefined
        }
        pagination={
          <TableFooter
            total={rows.length}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
          />
        }
      >
        <DataTable<Indexed<(typeof paged)[number]>>
          idAccessor="_i"
          records={paged}
          onRowClick={(r) => setSelected(r.id)}
          columns={[
            {
              accessor: 'display_name',
              title: t('partners.column.partner'),
              render: (r) => (
                <Stack gap={2}>
                  <Text role="bodySmall" fw={600}>{r.display_name}</Text>
                  <Text role="caption" c="var(--rr-color-text-muted)">{r.contact_email}</Text>
                </Stack>
              )
            },
            { accessor: 'organisation_id', title: t('partners.column.account'), render: (r) => <TenantName id={r.organisation_id} /> },
            { accessor: 'code', title: t('partners.column.code'), render: (r) => r.code ?? '—' },
            {
              accessor: 'status',
              title: t('partners.column.status'),
              render: (r) => (
                <Badge color={r.status === 'active' ? 'success' : r.status === 'pending_review' ? 'warning' : r.status === 'suspended' ? 'error' : undefined}>
                  {t(`partners.status.${r.status}`, r.status)}
                </Badge>
              )
            },
            {
              accessor: 'commission_percent',
              title: t('partners.column.share'),
              render: (r) => (
                <Group gap={4}>
                  <Text role="bodySmall">{r.commission_percent}%</Text>
                  {r.rate_is_override && <Badge color="info">{t('partners.ownRate')}</Badge>}
                </Group>
              )
            },
            { accessor: 'referrals_count', title: t('partners.column.referrals'), render: (r) => formatInteger(r.referrals_count) },
            { accessor: 'pending_minor', title: t('partners.column.pending'), render: (r) => formatMinor(r.pending_minor, 'ZAR') },
            { accessor: 'available_minor', title: t('partners.column.available'), render: (r) => formatMinor(r.available_minor, 'ZAR') },
            { accessor: 'applied_at', title: t('partners.column.applied'), render: (r) => formatDateTime(r.applied_at) }
          ]}
        />
      </TableShell>

      <AffiliateDrawer id={selected} onClose={() => setSelected(null)} />
    </Stack>
  );
}
